import { VoiceProduct } from '@shared/models/voice-product.model';
import { MbbProduct } from '@sales/modules/mbb/models/mbb-product.model';
import { HardwareProduct } from '@shared/models/hardware-product.model';
import { ProductHelperService } from '@app/sales/services/product-helper.service';
import { salesConstants } from '@app/sales/constants/sales-constants';
import { MobilePaymentType } from '@app/sales/enums/mobile-payment-type';
import { MobilePhoneProduct } from '@app/sales/models/mobilephone-product';
import { CaptureType } from '@app/sales/models/order-capture.model';
import { MbbDeliveryOptions } from '@app/sales/modules/mbb/models/mbb-delivery-options.model';
import { SubscriptionHelperService } from '@app/sales/services/subscription-helper.service';
import { Product } from '@app/shared/models/product.model';

export const getCaptureTypesForProduct = (product: Product, allVoiceProducts: VoiceProduct[]): CaptureType[] => {
  if (ProductHelperService.isAccessoryProduct(product)) {
    return getAccessoryCaptureTypes(product as HardwareProduct, allVoiceProducts);
  }
  if (ProductHelperService.isMbbProduct(product)) {
    return getMbbCaptureTypes(product as MbbProduct);
  }
  if (ProductHelperService.isMobileProduct(product)) {
    return getMobilePhoneCaptureTypes(product as MobilePhoneProduct, allVoiceProducts);
  }
  return getVoiceCaptureTypes(product as VoiceProduct);
};

export const getAccessoryCaptureTypes = (product: HardwareProduct, allVoiceProducts: VoiceProduct[]): CaptureType[] => {
  if (!product.isExistingSubscription) {
    if (product.selectedSubscription) {
      const connectedVoice = allVoiceProducts.find(p => p.id === product.voiceProductId);
      if (connectedVoice?.isExistingSubscription) {
        return [CaptureType.CreateAccessoryBundleWithExistingSubscription];
      }
      if (connectedVoice && !connectedVoice.isExistingSubscription) {
        switch (getNewSubscriptionCaptureType(connectedVoice)) {
          case CaptureType.CreateSubscriptionWithCustomerSelectedANumber:
            return [CaptureType.CreateAccessoryBundleWithCustomerSelectedANumber];
          case CaptureType.CreateSubscriptionWithPorting:
            return [CaptureType.CreateAccessoryBundleWithPorting];
        }
      }
    }
    return [CaptureType.AddEquipment];
  }
  return [CaptureType.NotImplemented];
};

export const getMbbCaptureTypes = (product: MbbProduct): CaptureType[] => {
  if (!product.isExistingSubscription) {
    switch (product.deliveryOption) {
      case MbbDeliveryOptions.SendRouterInPost:
        return [CaptureType.CreateMBBSubscriptionWithHardwareAndLogistics];
      case MbbDeliveryOptions.SellInStore:
        return [CaptureType.CreateMBBSubscriptionWithHardware];
      case MbbDeliveryOptions.AlreadyOwnRouter:
        return [CaptureType.CreateMBBSubscription];
      default:
        return [CaptureType.NotImplemented];
    }
  }
  if (product.existingSubscription && product.mbbSubscription?.id) {
    return [CaptureType.ChangeMbbSubscriptionOnExistingCustomer];
  }
  return [CaptureType.NotImplemented];
};

export const getMobilePhoneCaptureTypes = (product: MobilePhoneProduct, allVoiceProducts: VoiceProduct[]): CaptureType[] => {
  if (!product.isExistingSubscription) {
    if (product.paymentType === MobilePaymentType.PayNowWithoutSubscription) {
      return [CaptureType.AddEquipment];
    }
    if (
      [MobilePaymentType.EquipmentUpgrade, MobilePaymentType.PayNowWithSubscription].includes(product.paymentType) ||
      product.isManualRegistration
    ) {
      const connectedVoice = allVoiceProducts.find(p => p.id === product.voiceProductId);
      if (connectedVoice && !connectedVoice.isExistingSubscription) {
        switch (getNewSubscriptionCaptureType(connectedVoice)) {
          case CaptureType.CreateSubscriptionWithCustomerSelectedANumber:
            return [CaptureType.CreateCellPhoneBundleWithCustomerSelectedANumber];
          case CaptureType.CreateSubscriptionWithGeneratedANumber:
            return [CaptureType.CreateCellPhoneBundleWithGeneratedANumber];
          case CaptureType.CreateSubscriptionWithPorting:
            return [CaptureType.CreateCellPhoneBundleWithPorting];
        }
      } else if (connectedVoice?.isExistingSubscription && connectedVoice.subscription) {
        return [CaptureType.CreateCellPhoneBundleWithExistingSubscription];
      }
    }
  }
  return [CaptureType.NotImplemented];
};

export const getVoiceCaptureTypes = (product: VoiceProduct): CaptureType[] => {
  if (!product.isExistingSubscription) {
    return [getNewSubscriptionCaptureType(product)];
  }

  const list: CaptureType[] = [];

  if (product.offering?.id) {
    if (VoiceProduct.hasSubscriberChanged(product)) {
      list.push(CaptureType.ChangeSubscriptionOnExistingCustomerWithSubscriber);
    } else {
      list.push(CaptureType.ChangeSubscriptionOnExistingCustomer);
    }
  } else {
    if (SubscriptionHelperService.getChangedServicesOnExistingVoiceProduct(product).length > 0) {
      list.push(CaptureType.UpdateServicesOnExistingSubscription);
    }
    if (VoiceProduct.hasSubscriberChanged(product)) {
      list.push(CaptureType.ChangeSubscriberOnExistingSubscription);
    }
  }

  if (product.replaceVoiceSimIcc || product.replaceVoiceESim || product.replaceDataSimIcc || product.replaceTwinSimIcc) {
    list.push(CaptureType.ChangeSimCard);
  }

  return list.length ? list : [CaptureType.NotImplemented];
};

export const getNewSubscriptionCaptureType = (voice: VoiceProduct): CaptureType => {
  if (voice.phoneNumber.isPortingOrder && voice.phoneNumber.aNumber) {
    return CaptureType.CreateSubscriptionWithPorting;
  }
  if (!voice.phoneNumber.isPortingOrder && voice.phoneNumber.aNumber === salesConstants.isGeneratedANumber) {
    return CaptureType.CreateSubscriptionWithGeneratedANumber;
  }
  if (!voice.phoneNumber.isPortingOrder && voice.phoneNumber.aNumber !== salesConstants.isGeneratedANumber) {
    return CaptureType.CreateSubscriptionWithCustomerSelectedANumber;
  }
  return CaptureType.NotImplemented;
};

export const isVoiceProduct = (product: Product): product is VoiceProduct => {
  return (
    !ProductHelperService.isAccessoryProduct(product) &&
    !ProductHelperService.isMbbProduct(product) &&
    !ProductHelperService.isMobileProduct(product)
  );
};

export const isMobileProduct = (product: Product): product is MobilePhoneProduct => {
  return ProductHelperService.isMobileProduct(product);
};

export const hasAddedTwinOrDataSim = (product: Product): boolean => {
  const addedServices = VoiceProduct.addedServices(product as VoiceProduct);
  return addedServices.some(service => service.isDataSimCard || service.isTwinSimCard);
};
